import { CarOutlined, PlusOutlined, PrinterOutlined, SearchOutlined, UserOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Input, Tag } from "antd";
import type { InputRef } from "antd";
import { useDeferredValue, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppConfig } from "../../shared/config/AppConfigProvider";
import { customerName } from "../customers/customer-types";
import { repairOrdersApi } from "../repair-orders/repair-orders-api";

export function UniversalCommandBar() {
  const { text } = useAppConfig();
  const labels = text as unknown as Record<string, string>;
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<InputRef>(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const deferred = useDeferredValue(query.trim());
  const workspaceId = location.pathname.match(/^\/repair-orders\/([^/]+)\/workspace/)?.[1];
  const results = useQuery({
    queryKey: ["universal-command", deferred],
    enabled: deferred.length >= 2,
    queryFn: () => repairOrdersApi.arrivalSearch(deferred)
  });

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, []);

  const go = (path: string) => {
    setOpen(false);
    setQuery("");
    navigate(path);
  };

  return <div className="universal-command-bar">
    <Input
      ref={inputRef}
      allowClear
      prefix={<SearchOutlined />}
      suffix={<kbd>⌘K</kbd>}
      value={query}
      placeholder={text.commandBarPlaceholder}
      onFocus={() => setOpen(true)}
      onBlur={() => window.setTimeout(() => setOpen(false), 120)}
      onChange={event => {
        setQuery(event.target.value.toUpperCase());
        setOpen(true);
      }}
    />
    {open && <div className="universal-command-results">
      <header>{text.universalCommandBar}<small>{text.commandBarHint}</small></header>
      <button type="button" onMouseDown={event => event.preventDefault()} onClick={() => go("/repair-orders/new")}>
        <PlusOutlined /><span><b>{text.createRepairEvent}</b><small>{text.fastCheckInHint}</small></span>
      </button>
      {workspaceId && <button type="button" onMouseDown={event => event.preventDefault()} onClick={() => go(`/repair-orders/${workspaceId}/workspace/shop-work?print=settlement`)}>
        <PrinterOutlined /><span><b>{text.printInvoice}</b><small>{text.printSettlement}</small></span>
      </button>}
      {(results.data?.repairEvents ?? []).slice(0, 5).map(event => <button
        type="button"
        key={event.id}
        onMouseDown={mouseEvent => mouseEvent.preventDefault()}
        onClick={() => go(event.attention.workspacePath)}
      >
        <CarOutlined />
        <span>
          <b>{event.roNumber} · {event.vehicle.year} {event.vehicle.make} {event.vehicle.model}</b>
          <small>{customerName(event.customer)} · {labels[event.attention.nextAction] ?? event.attention.nextAction}</small>
        </span>
        <Tag>{labels[`attention${event.attention.state}`] ?? event.attention.state}</Tag>
      </button>)}
      {(results.data?.vehicles ?? []).filter(vehicle => !vehicle.activeRepairEvent).slice(0, 3).map(vehicle => <button
        type="button"
        key={vehicle.id}
        onMouseDown={mouseEvent => mouseEvent.preventDefault()}
        onClick={() => go(`/repair-orders/new?customerId=${vehicle.customerId}&vehicleId=${vehicle.id}`)}
      >
        <CarOutlined />
        <span><b>{vehicle.year} {vehicle.make} {vehicle.model}</b><small>{vehicle.vin} · {customerName(vehicle.customer)}</small></span>
        <Tag>{text.startNewRepairEvent}</Tag>
      </button>)}
      {(results.data?.customers ?? []).slice(0, 3).map(customer => <button
        type="button"
        key={customer.id}
        onMouseDown={mouseEvent => mouseEvent.preventDefault()}
        onClick={() => go(`/repair-orders/new?customerId=${customer.id}`)}
      >
        <UserOutlined />
        <span><b>{customerName(customer)}</b><small>{customer.phone}</small></span>
        <Tag>{text.selectVehicleNext}</Tag>
      </button>)}
      {deferred.length >= 2 && !results.isFetching
        && !results.data?.repairEvents.length && !results.data?.vehicles.length && !results.data?.customers.length
        && <p>{text.noSearchResults}</p>}
    </div>}
  </div>;
}
